import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';

// The sort keys of the label and grade columns; a score column sorts by its own key.
const c_LabelSortKey = 'label';
const c_GradeSortKey = 'grade';

// The grades best first, the order a grade sort runs in.
const c_GradeOrder = ['A', 'B', 'C', 'D'];

// The score columns, in display order, shared by every table of scored rows.
export const c_ScoreColumns = [
	{ key: 'answer_readiness_score', label: 'Answer Readiness' },
	{ key: 'semantic_clarity_score', label: 'Semantic Clarity' },
	{ key: 'entity_clarity_score', label: 'Entity Clarity' },
	{ key: 'content_depth_score', label: 'Content Depth' },
	{ key: 'technical_seo_score', label: 'Technical SEO' }
];

export const get_score_color_class = (value)=>{
	if(value <= 25){
		return 'score-red';
	}

	if(value <= 50){
		return 'score-orange';
	}

	if(value <= 75){
		return 'score-yellow';
	}

	return 'score-green';
};

// A grade is colored by its letter, so it always agrees with the grade bands the backend uses.
export const get_grade_color_class = (grade)=>{
	if(grade === 'D'){
		return 'score-red';
	}

	if(grade === 'C'){
		return 'score-orange';
	}

	if(grade === 'B'){
		return 'score-yellow';
	}

	return 'score-green';
};

// A table of scored rows: a label column, the grade, then the five scores. rows is [{ id, label, grade, scoring }];
// label_header names the first column (the page, the run date, or the site); on_open is called with the row clicked.
// A row can also carry sublabel, a second line under its label, and status_text, shown across the grade and score
// columns of a row without scoring; a row with neither scoring nor status_text is not shown. A row with openable
// false cannot be opened: it gets no click and no pointer.
// sortable, when true, makes each header sort the rows by its column when clicked: grades and scores best first,
// labels from A to Z, and a second click on the same header reverses the order. Until a header is clicked, the rows
// keep the order given; rows that tie keep it too.
function ScoreRowsTable({rows, label_header, on_open, sortable}){
	const [sort_key, set_sort_key] = useState(null);
	const [sort_descending, set_sort_descending] = useState(true);

	const on_header_click = (key)=>{
		if(!sortable){
			return;
		}

		if(key === sort_key){
			set_sort_descending(!sort_descending);
			return;
		}

		set_sort_key(key);
		set_sort_descending(key !== c_LabelSortKey);
	};

	// A row's value for the sort: higher is better for grades and scores; labels compare as text.
	const get_sort_value = (row, key)=>{
		if(key === c_GradeSortKey){
			const grade_index = c_GradeOrder.indexOf(row.grade);
			return (grade_index >= 0) ? (c_GradeOrder.length - grade_index) : -1;
		}

		const value = row.scoring ? Number(row.scoring[key]) : NaN;
		return isNaN(value) ? -1 : value;
	};

	const get_sorted_rows = ()=>{
		if(!sort_key){
			return rows;
		}

		const sorted = rows.slice();
		sorted.sort((first, second)=>{
			var comparison = 0;
			if(sort_key === c_LabelSortKey){
				comparison = String(first.label).localeCompare(String(second.label));
			}
			else{
				comparison = get_sort_value(first, sort_key) - get_sort_value(second, sort_key);
			}

			return sort_descending ? -comparison : comparison;
		});

		return sorted;
	};

	const render_score = (scoring, column)=>{
		const value = scoring[column.key];
		return (
			<Col key={column.key} className="site-page-score-col">
				<span className={'site-page-score ' + get_score_color_class(value)}>
					{Math.round(value) + '%'}
				</span>
			</Col>
		);
	};

	const render_grade = (row)=>{
		if(!row.grade){
			return (
				<Col className="site-page-score-col"></Col>
			);
		}

		return (
			<Col className="site-page-score-col">
				<span className={'site-page-grade ' + get_grade_color_class(row.grade)}>
					{row.grade}
				</span>
			</Col>
		);
	};

	const render_label = (row)=>{
		if(!row.sublabel){
			return (
				<Col className="site-page-url-col">{row.label}</Col>
			);
		}

		return (
			<Col className="site-page-url-col">
				{row.label}
				<div className="site-page-sublabel">{row.sublabel}</div>
			</Col>
		);
	};

	// A row with openable false opens nothing: it is not clickable and shows no pointer.
	const get_row_class = (row)=>{
		return (row.openable === false) ? 'page-analyses-row page-analyses-row-inert' : 'page-analyses-row';
	};

	const get_row_click = (row)=>{
		if(row.openable === false){
			return null;
		}

		return ()=>{ on_open(row); };
	};

	const render_row = (row)=>{
		if(!row.scoring && !row.status_text){
			return null;
		}

		if(!row.scoring){
			return (
				<Row key={row.id} className={get_row_class(row)} onClick={get_row_click(row)}>
					{render_label(row)}
					<Col className="site-page-status-col">{row.status_text}</Col>
				</Row>
			);
		}

		return (
			<Row key={row.id} className={get_row_class(row)} onClick={get_row_click(row)}>
				{render_label(row)}
				{render_grade(row)}
				{c_ScoreColumns.map((column)=>{ return render_score(row.scoring, column); })}
			</Row>
		);
	};

	// The header's text, with an arrow on the column the rows are sorted by.
	const render_header_text = (key, text)=>{
		if(sort_key !== key){
			return text;
		}

		return text + (sort_descending ? ' ▼' : ' ▲');
	};

	const get_header_class = (base_class)=>{
		return sortable ? base_class + ' site-page-header-sortable' : base_class;
	};

	const render_header = ()=>{
		return (
			<Row className="site-page-header-row">
				<Col
					className={get_header_class('site-page-url-col')}
					onClick={()=>{ on_header_click(c_LabelSortKey); }}>
					{render_header_text(c_LabelSortKey, label_header)}
				</Col>
				<Col
					className={get_header_class('site-page-score-col site-page-header-label')}
					onClick={()=>{ on_header_click(c_GradeSortKey); }}>
					{render_header_text(c_GradeSortKey, 'Grade')}
				</Col>
				{c_ScoreColumns.map((column)=>{
					return (
						<Col
							key={column.key}
							className={get_header_class('site-page-score-col site-page-header-label')}
							onClick={()=>{ on_header_click(column.key); }}>
							{render_header_text(column.key, column.label)}
						</Col>
					);
				})}
			</Row>
		);
	};

	if(!rows || (rows.length === 0)){
		return null;
	}

	return (
		<div className="page-analyses-rows">
			{render_header()}
			{get_sorted_rows().map((row)=>{ return render_row(row); })}
		</div>
	);
}

export default ScoreRowsTable;
